import React from 'react';
import styles from './Pagination.module.scss';

interface PaginationProps {
  current: number;
  total: number;
  children: (index: number, label: string, current: boolean) => React.ReactNode;
}

export default function Pagination({
  current = 1,
  total = 11,
  children,
}: PaginationProps): JSX.Element | null {
  const array = [...Array(total)];

  let newCurrent = current;
  if (current < 1) newCurrent = 1;
  if (current > total) newCurrent = total;

  if (total === 1) {
    return null;
  }

  if (total < 11) {
    return (
      <div className={styles.Pagination}>
        {children(newCurrent - 1, 'Back', current === 1)}
        {array.map((_, i) => children(i + 1, `${i + 1}`, current === i + 1))}
        {children(newCurrent + 1, 'Next', current === total)}
      </div>
    );
  }

  const pages: Array<{ type: string; number: number }> = array.reduce(
    (a, e, i) => {
      if (i === 2 && newCurrent === 1) {
        return [
          ...a,
          {
            type: '',
            number: 3,
          },
        ];
      }

      if (i === total - 3 && newCurrent === total) {
        return [...a, { type: '', number: total - 2 }];
      }

      if (
        (i === 0 && newCurrent > 2) ||
        (i === 1 && newCurrent > 3) ||
        (i === 2 && newCurrent > 4) ||
        (i < newCurrent + 1 && i + 3 > newCurrent) ||
        (i === total - 3 && newCurrent < total - 3) ||
        (i === total - 2 && newCurrent < total - 2) ||
        (i === total - 1 && newCurrent < total - 1)
      ) {
        return [
          ...a,
          {
            type: newCurrent === i + 1 ? 'current' : '',
            number: i + 1,
          },
        ];
      }

      if (
        (i === 3 && newCurrent > 5) ||
        (i === total - 4 && newCurrent < total - 4)
      ) {
        return [...a, { type: 'etc' }];
      }

      return a;
    },
    []
  );

  return (
    <div className={styles.Pagination}>
      {children(newCurrent - 1, 'Back', newCurrent === 1)}
      {pages.map(({ number, type }, i) => {
        if (type === 'etc') {
          return <span key={`etc${i}`}>...</span>;
        }
        return children(number, `${number}`, type === 'current');
      })}
      {children(newCurrent + 1, 'Next', newCurrent === total)}
    </div>
  );
}
